import * as React from "react"
import { type LucideIcon } from "lucide-react"
import { Link, usePage } from "@inertiajs/react"
import * as Collapsible from "@radix-ui/react-collapsible"
import { ChevronRightIcon } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"

interface NavChild {
  title: string
  url: string
  icon?: LucideIcon
}

interface NavItem {
  title: string
  url?: string
  icon?: LucideIcon
  activePattern?: string
  children?: NavChild[]
}

function isItemActive(url: string, item: { url?: string; activePattern?: string }) {
  if (!item.url) return false

  if (item.activePattern) {
    const pattern = new RegExp(item.activePattern)
    return pattern.test(url)
  } else if (item.url === '/admin') {
    return url === item.url
  } else if (item.url === '/organization') {
    return url === '/organization' || url === '/organization/edit'
  } else if (item.url.match(/^\/projects\/\d+$/)) {
    return url === item.url
  } else {
    return url.startsWith(item.url)
  }
}

export function NavMain({
  items,
  ...props
}: {
  items: NavItem[]
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const { url } = usePage()
  const { isMobile, state } = useSidebar()
  const isCollapsed = !isMobile && state === "collapsed"

  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            if (item.children) {
              const hasActiveChild = item.children.some(
                (child) => isItemActive(url, child)
              )

              if (isCollapsed) {
                return (
                  <SidebarMenuItem key={item.title}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton tooltip={item.title} isActive={hasActiveChild}>
                          {item.icon && <item.icon />}
                          <span>{item.title}</span>
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent side="right" align="start" sideOffset={4}>
                        {item.children.map((child) => (
                          <DropdownMenuItem key={child.title} asChild>
                            <Link href={child.url}>
                              {child.icon && <child.icon />}
                              <span>{child.title}</span>
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                )
              }

              return (
                <Collapsible.Root
                  key={item.title}
                  asChild
                  defaultOpen={hasActiveChild}
                  className="group/collapsible"
                >
                  <SidebarMenuItem>
                    <Collapsible.Trigger asChild>
                      <SidebarMenuButton tooltip={item.title} isActive={hasActiveChild}>
                        {item.icon && <item.icon />}
                        <span className="truncate">{item.title}</span>
                        <ChevronRightIcon className="ml-auto shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </Collapsible.Trigger>
                    <Collapsible.Content>
                      <SidebarMenuSub>
                        {item.children.map((child) => (
                          <SidebarMenuSubItem key={child.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={isItemActive(url, child)}
                            >
                              <Link href={child.url}>
                                {child.icon && <child.icon />}
                                <span>{child.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </Collapsible.Content>
                  </SidebarMenuItem>
                </Collapsible.Root>
              )
            }

            const isActive = isItemActive(url, item)

            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton asChild tooltip={item.title} isActive={isActive}>
                  <Link href={item.url!}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}
